import React, {
  FC,
  useState,
  useEffect,
  useCallback,
  useMemo,
  Fragment
} from 'react'
import clsx from 'clsx'
import { oembed } from '@loomhq/loom-embed'
import { Icon } from '@iconify/react'
import { Prompt } from 'react-router-dom'
import get from 'lodash.get'

import { Tooltip, Button, RichTextEditor, Modal } from '../../atoms'
import { useShowMessage } from '../../providers'

import { formatBytesToHumanReadableString } from '@pepper/utils/src/function/misc'
import { secondsToHumanReadableTime } from '@pepper/utils/src/function/timeHelpers'
import { fetchFile } from '@pepper/utils/src/api/files/files'

import { OEmbedInterface } from '../../molecules'
import { useLoomRecorder, LoomRecorderStatus } from '../../hooks'
import FileUpload from './FileUpload/FileUpload'
import styles from './_.module.css'

import type { LoomVideo } from '@loomhq/loom-sdk'

interface ExtendedAttachment extends Attachment {
  status?: string
  size: number
}
interface Props {
  className?: string
  onChange: (
    values: RichTextValues | string,
    charCount?: number,
    plainText?: string
  ) => void
  fieldData?:
    | {
        richText: string
        attachments: ExtendedAttachment[]
        loomUrls: string[]
      }
    | string
  allowedFiles?: string[]
  placeholder?: string
  isAttachFile?: boolean
  isVideoRecord?: boolean
  disabled?: boolean
  shouldDisplayLoomURLs?: boolean
  loomButtonId?: string
  hideVideoUploadIcon?: boolean
  mentionData?: RemirrorMentions[]
  maxLength?: number
  minLength?: number
  onMount?: () => void
}

function isLoomValue(value: any): value is {
  richText: string
  attachments: ExtendedAttachment[]
  loomUrls: string[]
} {
  return typeof value !== 'string'
}

export const RichText: FC<Props> = ({
  className,
  onChange,
  fieldData = {
    richText: '',
    attachments: [],
    loomUrls: []
  },
  placeholder = 'Add a comment...',
  allowedFiles,
  isAttachFile = false,
  isVideoRecord = false,
  disabled = false,
  shouldDisplayLoomURLs = true,
  loomButtonId = 'Add Loom Video',
  hideVideoUploadIcon,
  mentionData = [],
  maxLength,
  minLength,
  onMount
}) => {
  const [isOverlayPresent, setIsOverlayPresent] = useState(false)
  const [charCount, setCharCount] = useState<number | undefined>()
  const [plaintext, setPlaintext] = useState<string | undefined>()

  const loomButtonIdString = `ADD_VIDEO_RECORDING-${loomButtonId}`
  const button = document.getElementById(loomButtonIdString)
  const [buttonRef, { setupResult: loomSetup }] = useLoomRecorder(button, {
    insertButtonText: 'Add Recording'
  })
  const showMessage = useShowMessage()

  const onAddVideoClick = useCallback(() => {
    if (loomSetup?.status === LoomRecorderStatus.SUCCESS) {
      if (isLoomValue(fieldData)) {
        loomSetup.sdkButton.on('recording-start', () =>
          setIsOverlayPresent(true)
        )
        loomSetup.sdkButton.on('insert-click', (video: LoomVideo) => {
          const _loomUrls = [...(fieldData?.loomUrls ?? []), video.sharedUrl]
          onChange({ ...fieldData, loomUrls: _loomUrls })
          setIsOverlayPresent(false)
        })
        loomSetup.sdkButton.on('cancel', () => {
          setIsOverlayPresent(false)
        })
        loomSetup.sdkButton.on('complete', () => {
          setIsOverlayPresent(false)
        })
      }
    }
  }, [loomSetup, fieldData, onChange])

  const handleRichTextChange = (
    editorHtml: string,
    charCount?: number,
    plainText?: string
  ) => {
    const value = editorHtml.trim() !== '' ? editorHtml : ''
    if (isLoomValue(fieldData)) {
      setPlaintext(plainText)
      if (fieldData.richText === value) return
      onChange({ ...fieldData, richText: value }, charCount, plainText)
    } else {
      if (fieldData === value) return
      onChange(value, charCount, plainText)
    }

    if (typeof plainText !== 'string') return

    setCharCount(plainText.length)
  }

  const removeLoomVideo = (loomUrl: string) => {
    if (disabled) return
    if (isLoomValue(fieldData)) {
      const _loomUrls = fieldData?.loomUrls?.filter(url => url !== loomUrl)
      onChange({ ...fieldData, loomUrls: [..._loomUrls] })
    }
  }

  const removeFile = (index: number): void => {
    if (disabled) return
    if (isLoomValue(fieldData)) {
      const _attachments = fieldData?.attachments?.filter((_, i) => i !== index)
      onChange({ ...fieldData, attachments: [..._attachments] }, charCount)
    }
  }

  const handleDownloadFile = async (fileId: string, fileName: string) => {
    try {
      if (typeof fileId !== 'string') {
        throw new Error('Not able to download file')
      }

      const fileResponse = await fetchFile({ id: fileId })
      const objectURL = URL.createObjectURL(fileResponse.data)

      const link = document.createElement('a')
      link.href = objectURL
      link.setAttribute('download', fileName)
      link.click()
    } catch (error) {
      showMessage({ type: 'error', message: error.message })
    }
  }

  const getFileActionButton = (file: ExtendedAttachment, index: number) => {
    if (file.status === 'uploading') {
      return (
        <Button size="small" type="text" disabled={disabled}>
          <Icon icon="line-md:loading-loop" width="18" height="18" />
        </Button>
      )
    }

    if (file.status === 'error') {
      return (
        <Button
          className={styles.closeBtn}
          size="small"
          type="text"
          onClick={() => removeFile(index)}
          disabled={disabled}
        >
          <Icon
            icon="lucide:alert-circle"
            width="18"
            height="18"
            color="var(--color-orange-700)"
          />
        </Button>
      )
    }

    return (
      <Fragment>
        <Button
          size="small"
          type="text"
          onClick={() => handleDownloadFile(file.id, file.name)}
        >
          <Icon
            icon="lucide:download"
            width="18"
            height="18"
            color="var(--color-gray-900)"
          />
        </Button>
        <Button
          className={styles.closeBtn}
          onClick={() => removeFile(index)}
          size="small"
          type="text"
          disabled={disabled}
        >
          <Icon
            icon="lucide:x"
            width="18"
            height="18"
            color="var(--color-gray-900)"
          />
        </Button>
      </Fragment>
    )
  }

  const editorData = useMemo(() => {
    if (isLoomValue(fieldData)) {
      return fieldData.richText ? fieldData.richText : ''
    }
    return fieldData ?? ''
  }, [fieldData])

  useEffect(() => {
    onMount?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div
      className={clsx(
        styles.richTextContainer,
        className,
        disabled && styles.richTextContainerDisabled
      )}
    >
      <Prompt
        when={isOverlayPresent}
        message={() => {
          if (loomSetup?.status === LoomRecorderStatus.SUCCESS) {
            loomSetup.sdkButton.endRecording()
          }
          return true
        }}
      />
      <div id="rich-text-editor" className={clsx(styles.richTextEditor)}>
        <RichTextEditor
          placeholder={placeholder}
          toolbar={true}
          setHtmlData={true}
          getHtmlData={true}
          miniEditor={true}
          updateData={handleRichTextChange}
          loadData={editorData}
          editable={!disabled}
          mentionData={mentionData}
          maxLength={maxLength}
          minLength={minLength}
        />
      </div>

      <div className={clsx(styles.addOns)}>
        {isAttachFile ? (
          <FileUpload
            multipleSelection={true}
            multipleUpload={true}
            files={isLoomValue(fieldData) ? fieldData?.attachments ?? [] : []}
            setFiles={files => {
              const richText =
                typeof fieldData === 'string'
                  ? fieldData
                  : get(fieldData, 'richText', '')
              const loomUrls = get(fieldData, 'loomUrls', [])
              const attachments = [...files]
              onChange(
                { richText, loomUrls, attachments },
                charCount,
                plaintext
              )
            }}
            allowedFiles={allowedFiles}
            disabled={disabled}
          />
        ) : null}
        {isVideoRecord &&
        isLoomValue(fieldData) &&
        !hideVideoUploadIcon &&
        fieldData?.loomUrls?.length === 0 &&
        loomSetup?.status !== LoomRecorderStatus.SDK_UNSUPPORTED ? (
          <Button
            ref={buttonRef}
            id={loomButtonIdString}
            type="link"
            onClick={onAddVideoClick}
            className={styles.addOnButton}
            disabled={disabled}
          >
            <Icon icon="lucide:video" height="16" width="16" />
            <span className={styles.addOnButtonText}>Video Recording</span>
          </Button>
        ) : null}
      </div>
      {isLoomValue(fieldData) && fieldData?.attachments?.length > 0 ? (
        <div className={styles.addOnsList}>
          {fieldData?.attachments?.map((file, index) => (
            <div key={file.id || file.name} className={styles.lineWrap}>
              <div className={styles.addOnName}>
                <Icon
                  icon="lucide:file-text"
                  width="16"
                  height="16"
                  className={styles.addOnIcon}
                />
                <Tooltip title={file.name}>
                  <span className={styles.text}>{file.name}</span>
                </Tooltip>
                {file.size && (
                  <span className={styles.addOnSize}>
                    {`(${formatBytesToHumanReadableString(file.size, 0)})`}
                  </span>
                )}
              </div>
              <div className={styles.cancelIcon}>
                {getFileActionButton(file, index)}
              </div>
            </div>
          ))}
        </div>
      ) : null}
      {shouldDisplayLoomURLs &&
      isLoomValue(fieldData) &&
      fieldData?.loomUrls?.length > 0 ? (
        <div className={styles.addOnsList}>
          {fieldData?.loomUrls?.map((loomUrl: string, index: number) => (
            <LoomMatchDisplay
              loomUrl={loomUrl}
              key={index}
              removeVideo={removeLoomVideo}
            />
          ))}
        </div>
      ) : null}
      <Modal
        visible={isOverlayPresent}
        maskClosable={false}
        modalRender={() => null}
      />
    </div>
  )
}

const LoomMatchDisplay = ({
  loomUrl,
  removeVideo,
  disabled = false
}: {
  loomUrl: string
  removeVideo: (loomUrl: string) => void
  disabled?: boolean
}) => {
  const [loomVideo, setLoomVideo] = useState<OEmbedInterface>()
  useEffect(() => {
    const updateLoomVideoData = async () => {
      const loomVideo = await oembed(loomUrl)
      setLoomVideo(loomVideo)
    }
    updateLoomVideoData()
  }, [loomUrl])

  return (
    <div className={styles.lineWrap}>
      <div className={styles.addOnName}>
        <Icon
          icon="lucide:video"
          height="16"
          width="16"
          className={styles.addOnIconLoom}
        />
        <Tooltip title={loomVideo?.title}>
          <span className={styles.text}>{loomVideo?.title}</span>
        </Tooltip>
        <span className={styles.addOnSize}>
          {`(${secondsToHumanReadableTime(loomVideo?.duration || 0)})`}
        </span>
      </div>
      <div className={styles.cancelIcon}>
        <Button
          size="small"
          type="text"
          onClick={() => removeVideo(loomUrl)}
          className={styles.closeBtn}
          disabled={disabled}
        >
          <Icon icon="lucide:x" width="18" height="18" />
        </Button>
      </div>
    </div>
  )
}
